import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { userInviteService } from "@/services/breeze/UserInviteService";
import { MyUser, ConfirmUserRequest } from "@/types/breeze/User";
import InputField from "@/components/form/InputField";
import {
  Box,
  Card,
  Flex,
  Text,
  Heading,
  Container,
  Button,
} from "@radix-ui/themes";

const validationSchema = Yup.object({
  first_name: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  last_name: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[^A-Za-z0-9]/,
      "Password must contain at least one special character",
    ),
  confirmPassword: Yup.string()
    .required("Password confirmation is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

export default function AcceptInvitePage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [inviteUser, setInviteUser] = useState<MyUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const inviteId = params.inviteId as string;
  const orgIdParam = searchParams.get("org_id");

  useEffect(() => {
    const fetchInviteUser = async () => {
      try {
        const user = await userInviteService.getUserByInviteToken(
          Number(orgIdParam),
          inviteId,
        );
        setInviteUser(user);
      } catch (error) {
        console.error("Error fetching invite user:", error);
        setError(
          "Invalid or expired invite link. Please contact your administrator.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (inviteId && !inviteUser) {
      fetchInviteUser();
    }
  }, [inviteId, orgIdParam]);

  const handleSubmit = async (
    values: ConfirmUserRequest & { confirmPassword: string },
  ) => {
    setIsSubmitting(true);
    setError("");

    try {
      // Confirm the user invitation
      await userInviteService.confirmUserByInviteToken(
        Number(orgIdParam),
        inviteId,
        {
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
        },
      );

      // Show success message instead of logging in
      setIsSuccess(true);
    } catch (error) {
      console.error("Error accepting invite:", error);
      setError(
        "Failed to accept invite. Please try again or contact your administrator.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium">
                Loading invite...
              </Heading>
              <Text color="gray" align="center">
                Please wait while we verify your invitation.
              </Text>
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  if (error && !inviteUser) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium" color="red">
                Invalid Invite
              </Heading>
              <Text color="gray" align="center">
                {error}
              </Text>
              <Text color="gray" align="center">
                If you believe this is an error, please contact your
                administrator for a new link.
              </Text>
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  // Show success message after account setup
  if (isSuccess) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium" color="green">
                Account Setup Complete
              </Heading>
              <Text color="gray" align="center">
                Your account has been successfully created. You can now log in
                to your application using your email and password.
              </Text>
              <Text color="gray" align="center" size="2">
                Please return to your application to log in.
              </Text>
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      size="1"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <Card style={{ width: "100%", maxWidth: "28rem" }}>
        <Box p="6">
          <Flex direction="column" gap="6">
            <Flex direction="column" align="center" gap="2">
              <Heading size="7" weight="bold" align="center">
                Welcome to {inviteUser?.tenant_name || "your organisation"}
              </Heading>
              {inviteUser && (
                <Text color="gray" align="center" size="2">
                  You&apos;ve been invited to join as{" "}
                  <Text weight="medium">{inviteUser.email}</Text>
                </Text>
              )}
            </Flex>

            {error && (
              <Box
                p="3"
                style={{
                  backgroundColor: "var(--red-a3)",
                  border: "1px solid var(--red-a6)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text color="red" size="2">
                  {error}
                </Text>
              </Box>
            )}

            <Formik
              initialValues={{
                first_name: inviteUser?.first_name || "",
                last_name: inviteUser?.last_name || "",
                password: "",
                confirmPassword: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {() => (
                <Form>
                  <Flex direction="column" gap="4">
                    <Flex gap="3">
                      <Box style={{ flex: 1 }}>
                        <InputField
                          name="first_name"
                          label="First Name"
                          type="text"
                          required
                          options={{ placeholder: "Enter your first name" }}
                        />
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <InputField
                          name="last_name"
                          label="Last Name"
                          type="text"
                          required
                          options={{ placeholder: "Enter your last name" }}
                        />
                      </Box>
                    </Flex>

                    <InputField
                      name="password"
                      label="Password"
                      type="password"
                      required
                      options={{
                        placeholder: "Create a secure password",
                        masked: true,
                      }}
                      description="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
                    />

                    <InputField
                      name="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      required
                      options={{
                        placeholder: "Confirm your password",
                        masked: true,
                      }}
                    />

                    <Button
                      type="submit"
                      loading={isSubmitting}
                      style={{ width: "100%" }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Accepting Invitation..."
                        : "Accept Invitation"}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </Box>
      </Card>
    </Container>
  );
}
